import styled, { css } from 'styled-components'
import { theme as uiTheme } from '../../../theme'

const getSelectorTheme = (runtimeTheme?: any) => ({
  colors: {
    primary: runtimeTheme?.colors?.primary ?? uiTheme.colors.primary,
    primaryLight:
      runtimeTheme?.colors?.primaryLight ??
      runtimeTheme?.colors?.primaryContainer ??
      uiTheme.colors.primaryLight,
    text: {
      primary:
        runtimeTheme?.colors?.text?.primary ??
        runtimeTheme?.colors?.onSurface ??
        uiTheme.colors.text.primary,
      secondary:
        runtimeTheme?.colors?.text?.secondary ??
        runtimeTheme?.colors?.onSurfaceVariant ??
        uiTheme.colors.text.secondary,
      tertiary:
        runtimeTheme?.colors?.text?.tertiary ??
        runtimeTheme?.colors?.onSurfaceVariant ??
        uiTheme.colors.text.tertiary,
    },
    background: {
      primary:
        runtimeTheme?.colors?.background?.primary ??
        runtimeTheme?.colors?.surface ??
        uiTheme.colors.background.primary,
      tertiary:
        runtimeTheme?.colors?.background?.tertiary ??
        runtimeTheme?.colors?.surfaceVariant ??
        uiTheme.colors.background.tertiary,
    },
    border: {
      default:
        runtimeTheme?.colors?.border?.default ??
        runtimeTheme?.colors?.outline ??
        uiTheme.colors.border.default,
      light:
        runtimeTheme?.colors?.border?.light ??
        runtimeTheme?.colors?.outlineVariant ??
        uiTheme.colors.border.light,
    },
  },
  typography: {
    fontSize: {
      micro:
        runtimeTheme?.typography?.fontSize?.micro ??
        runtimeTheme?.typography?.fontSize?.small ??
        uiTheme.typography.fontSize.micro,
      small:
        runtimeTheme?.typography?.fontSize?.small ??
        uiTheme.typography.fontSize.small,
      body:
        runtimeTheme?.typography?.fontSize?.body ??
        runtimeTheme?.typography?.fontSize?.base ??
        uiTheme.typography.fontSize.body,
    },
    fontWeight: {
      normal:
        runtimeTheme?.typography?.fontWeight?.normal ??
        uiTheme.typography.fontWeight.normal,
      medium:
        runtimeTheme?.typography?.fontWeight?.medium ??
        uiTheme.typography.fontWeight.medium,
      semibold:
        runtimeTheme?.typography?.fontWeight?.semibold ??
        runtimeTheme?.typography?.fontWeight?.bold ??
        uiTheme.typography.fontWeight.semibold,
    },
    lineHeight: {
      tight:
        runtimeTheme?.typography?.lineHeight?.tight ??
        uiTheme.typography.lineHeight.tight,
      normal:
        runtimeTheme?.typography?.lineHeight?.normal ??
        uiTheme.typography.lineHeight.normal,
    },
  },
  spacing: {
    micro: runtimeTheme?.spacing?.micro ?? uiTheme.spacing.micro,
    xsmall: runtimeTheme?.spacing?.xsmall ?? uiTheme.spacing.xsmall,
    small: runtimeTheme?.spacing?.small ?? uiTheme.spacing.small,
    default: runtimeTheme?.spacing?.default ?? uiTheme.spacing.default,
  },
  borderRadius: {
    large: runtimeTheme?.borderRadius?.large ?? uiTheme.borderRadius.large,
    xlarge:
      runtimeTheme?.borderRadius?.xlarge ??
      runtimeTheme?.borderRadius?.large ??
      uiTheme.borderRadius.xlarge,
    round: runtimeTheme?.borderRadius?.round ?? uiTheme.borderRadius.round,
    circle: runtimeTheme?.borderRadius?.circle ?? uiTheme.borderRadius.circle,
  },
  shadows: {
    sm:
      runtimeTheme?.shadows?.sm ??
      runtimeTheme?.shadows?.light ??
      uiTheme.shadows.sm,
    xl:
      runtimeTheme?.shadows?.xl ??
      runtimeTheme?.shadows?.heavy ??
      uiTheme.shadows.xl,
    focus: runtimeTheme?.shadows?.focus ?? uiTheme.shadows.focus,
  },
  transitions: {
    fast: runtimeTheme?.transitions?.fast ?? uiTheme.transitions.fast,
  },
  zIndex: {
    popover: runtimeTheme?.zIndex?.popover ?? uiTheme.zIndex.popover,
  },
})

export const SelectorRoot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

export const GearButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: ${({ $open, theme }) =>
    $open
      ? getSelectorTheme(theme).colors.primaryLight
      : getSelectorTheme(theme).colors.background.primary};
  border: 1px solid
    ${({ $open, theme }) =>
      $open
        ? getSelectorTheme(theme).colors.primary
        : getSelectorTheme(theme).colors.border.default};
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.circle};
  color: ${({ $open, theme }) =>
    $open
      ? getSelectorTheme(theme).colors.primary
      : getSelectorTheme(theme).colors.text.secondary};
  box-shadow: ${({ $open, theme }) =>
    $open ? getSelectorTheme(theme).shadows.sm : 'none'};
  cursor: pointer;
  transition:
    background ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    border-color ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    box-shadow ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    color ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    transform ${({ theme }) => getSelectorTheme(theme).transitions.fast};

  &:hover {
    background: ${({ theme }) => getSelectorTheme(theme).colors.primaryLight};
    color: ${({ theme }) => getSelectorTheme(theme).colors.primary};
    border-color: ${({ theme }) => getSelectorTheme(theme).colors.primary};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => getSelectorTheme(theme).shadows.focus};
  }
`

/** Labelled trigger ("basis: living area") used where the gear alone says too little. */
export const PillButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.xsmall};
  height: 24px;
  padding: 0 ${({ theme }) => getSelectorTheme(theme).spacing.small};
  background: ${({ theme }) =>
    getSelectorTheme(theme).colors.background.primary};
  border: 1px solid
    ${({ theme, $open }) =>
      $open
        ? getSelectorTheme(theme).colors.primary
        : getSelectorTheme(theme).colors.border.default};
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.large};
  color: ${({ theme }) => getSelectorTheme(theme).colors.text.primary};
  font-family: inherit;
  font-size: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontSize.small};
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color
    ${({ theme }) => getSelectorTheme(theme).transitions.fast};

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => getSelectorTheme(theme).colors.primary};
    outline: none;
  }
`

export const Popover = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: ${({ theme }) => getSelectorTheme(theme).zIndex.popover};
  width: min(292px, calc(100vw - 32px));
  padding: ${({ theme }) => getSelectorTheme(theme).spacing.small};
  background: ${({ theme }) =>
    getSelectorTheme(theme).colors.background.primary};
  border: 1px solid
    ${({ theme }) => getSelectorTheme(theme).colors.border.default};
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.xlarge};
  box-shadow: ${({ theme }) => getSelectorTheme(theme).shadows.xl};
  text-align: left;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 12px;
    width: 0;
    height: 0;
  }

  &::before {
    top: -7px;
    border-right: 7px solid transparent;
    border-bottom: 7px solid
      ${({ theme }) => getSelectorTheme(theme).colors.border.default};
    border-left: 7px solid transparent;
  }

  &::after {
    top: -6px;
    border-right: 6px solid transparent;
    border-bottom: 6px solid
      ${({ theme }) => getSelectorTheme(theme).colors.background.primary};
    border-left: 6px solid transparent;
  }
`

export const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.default};
  min-height: 28px;
  padding: ${({ theme }) => getSelectorTheme(theme).spacing.small}
    ${({ theme }) => getSelectorTheme(theme).spacing.default};
`

export const PopoverRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.small};
  padding: ${({ theme }) => getSelectorTheme(theme).spacing.xsmall}
    ${({ theme }) => getSelectorTheme(theme).spacing.small};
`

export const RowLabel = styled.span`
  min-width: 0;
  font-size: ${({ theme }) => getSelectorTheme(theme).typography.fontSize.body};
  font-weight: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontWeight.semibold};
  color: ${({ theme }) => getSelectorTheme(theme).colors.text.primary};
  line-height: ${({ theme }) =>
    getSelectorTheme(theme).typography.lineHeight.tight};
`

export const Divider = styled.div`
  height: 1px;
  margin: 0 ${({ theme }) => getSelectorTheme(theme).spacing.xsmall}
    ${({ theme }) => getSelectorTheme(theme).spacing.small};
  background: ${({ theme }) => getSelectorTheme(theme).colors.border.light};
`

export const SectionLabel = styled.div`
  padding: 0 ${({ theme }) => getSelectorTheme(theme).spacing.default}
    ${({ theme }) => getSelectorTheme(theme).spacing.xsmall};
  font-size: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontSize.micro};
  font-weight: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontWeight.semibold};
  color: ${({ theme }) => getSelectorTheme(theme).colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.7px;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.micro};
`

export const OptionRow = styled.button<{
  $selected?: boolean
  $disabled?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.default};
  width: 100%;
  min-height: 30px;
  padding: ${({ theme }) => getSelectorTheme(theme).spacing.small}
    ${({ theme }) => getSelectorTheme(theme).spacing.default};
  background: ${({ $selected, theme }) =>
    $selected ? getSelectorTheme(theme).colors.primaryLight : 'transparent'};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'rgba(0, 173, 230, 0.26)' : 'transparent'};
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.large};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    background ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    border-color ${({ theme }) => getSelectorTheme(theme).transitions.fast},
    color ${({ theme }) => getSelectorTheme(theme).transitions.fast};

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected
        ? getSelectorTheme(theme).colors.primaryLight
        : getSelectorTheme(theme).colors.background.tertiary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => getSelectorTheme(theme).shadows.focus};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 1;

      &:hover {
        background: transparent;
      }
    `}
`

export const OptionLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => getSelectorTheme(theme).spacing.default};
  min-width: 0;
`

export const Radio = styled.span<{ $selected?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.circle};
  border: 1.5px solid
    ${({ $selected, $disabled, theme }) =>
      $disabled
        ? getSelectorTheme(theme).colors.border.light
        : $selected
          ? getSelectorTheme(theme).colors.primary
          : getSelectorTheme(theme).colors.border.default};
  background: transparent;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${({ theme }) =>
      getSelectorTheme(theme).borderRadius.circle};
    background: ${({ $selected, theme }) =>
      $selected ? getSelectorTheme(theme).colors.primary : 'transparent'};
  }
`

export const OptionLabel = styled.span<{
  $selected?: boolean
  $disabled?: boolean
}>`
  font-size: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontSize.small};
  font-weight: ${({ $selected, theme }) =>
    $selected
      ? getSelectorTheme(theme).typography.fontWeight.semibold
      : getSelectorTheme(theme).typography.fontWeight.normal};
  color: ${({ $disabled, theme }) =>
    $disabled
      ? getSelectorTheme(theme).colors.text.tertiary
      : getSelectorTheme(theme).colors.text.primary};
  line-height: ${({ theme }) =>
    getSelectorTheme(theme).typography.lineHeight.tight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OptionValue = styled.span<{ $disabled?: boolean }>`
  font-size: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontSize.small};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontWeight.medium};
  color: ${({ $disabled, theme }) =>
    $disabled
      ? getSelectorTheme(theme).colors.text.tertiary
      : getSelectorTheme(theme).colors.text.secondary};
  white-space: nowrap;
`

export const ToggleTrack = styled.button<{ $on: boolean }>`
  position: relative;
  width: 36px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => getSelectorTheme(theme).borderRadius.round};
  background: ${({ $on, theme }) =>
    $on
      ? getSelectorTheme(theme).colors.primary
      : getSelectorTheme(theme).colors.border.default};
  cursor: pointer;
  transition: background
    ${({ theme }) => getSelectorTheme(theme).transitions.fast};
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? '18px' : '2px')};
    width: 16px;
    height: 16px;
    border-radius: ${({ theme }) =>
      getSelectorTheme(theme).borderRadius.circle};
    background: ${({ theme }) =>
      getSelectorTheme(theme).colors.background.primary};
    box-shadow: ${({ theme }) => getSelectorTheme(theme).shadows.sm};
    transition: left ${({ theme }) => getSelectorTheme(theme).transitions.fast};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => getSelectorTheme(theme).shadows.focus};
  }
`

export const FallbackHint = styled.div`
  padding: ${({ theme }) => getSelectorTheme(theme).spacing.small}
    ${({ theme }) => getSelectorTheme(theme).spacing.default} 0;
  margin-top: ${({ theme }) => getSelectorTheme(theme).spacing.small};
  font-size: ${({ theme }) =>
    getSelectorTheme(theme).typography.fontSize.micro};
  color: ${({ theme }) => getSelectorTheme(theme).colors.text.tertiary};
  line-height: ${({ theme }) =>
    getSelectorTheme(theme).typography.lineHeight.normal};
`
